import { SystemPageKey } from '@internal/page/SystemPageKey'
import { Locale, TLocale } from '@internal/utils/machine/i18n/Locale'
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName'
import { searchModelsToQueryParams } from '@internal/utils/routing/getSearchResultsUrl'
import {
  hotDealsRoute,
  matchSearchRoute,
} from '@internal/utils/routing/routingTable'
import { urlFor } from '@internal/utils/routing/urls/urlFor'
import { useRouter } from 'next/router'
import { PageContext } from 'page/PageContext'
import { useContext, useMemo } from 'react'
import { PageTypeKey } from 'utils/page/PageTypeKey'
import { TLocalizedUrlMap } from './TLocalizedUrlMap'

// this used to use urlFor, but that created a problem in the middleware
export const getLocalizedUrlMapForStartPage = (): TLocalizedUrlMap => ({
  [Locale.de]: urlFor(Locale.de, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.en]: urlFor(Locale.en, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.fr]: urlFor(Locale.fr, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.el]: urlFor(Locale.el, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.it]: urlFor(Locale.it, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.pl]: urlFor(Locale.pl, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.ro]: urlFor(Locale.ro, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.ru]: urlFor(Locale.ru, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.hu]: urlFor(Locale.hu, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.es]: urlFor(Locale.es, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.bg]: urlFor(Locale.bg, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.sl]: urlFor(Locale.sl, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.sr]: urlFor(Locale.sr, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.da]: urlFor(Locale.da, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.cs]: urlFor(Locale.cs, {
    key: SystemPageKey.StartPage,
  }),
  [Locale.lt]: urlFor(Locale.lt, {
    key: SystemPageKey.StartPage,
  }),
})

// generates localized url maps dynamically in order to be able to switch the language at any time and stay on the localised page
export const useLocalizedUrlMap = () => {
  const {
    pageData,
    localizedUrlMap,
    locale: currentLocale,
  } = useContext(PageContext)
  const { asPath, query } = useRouter()
  const { getMachineTypeForMachineTypeSlug } = useMachineTypeName()

  const localizations = useMemo(() => {
    let map: TLocalizedUrlMap = {
      // for the language switcher we need a url to swith to for every languages (even if that will be the start page)
      ...getLocalizedUrlMapForStartPage(),
      ...localizedUrlMap,
    }

    if (!pageData) {
      return map
    }

    switch (pageData.type) {
      case PageTypeKey.Page: {
        return map
      }
      case PageTypeKey.SystemPage: {
        if (pageData.key === SystemPageKey.Dashboard) {
          const dashboardRoute = asPath.split('#')[1] || ''

          for (const localeKey of Object.keys(Locale)) {
            const locale = localeKey as TLocale
            map[locale] = urlFor(locale, {
              key: SystemPageKey.Dashboard,
              dashboardRoute,
            })
          }
        } else if (pageData.key === SystemPageKey.SearchResults) {
          const url = urlFor(currentLocale, asPath.split('?')[0])
          const { slugs, ...queryParams } = query

          if (pageData.machineCategoryData?.url === url) {
            for (const key of Object.keys(map)) {
              const locale = key as TLocale
              const route = map[locale]
              if (route) {
                map[locale] = urlFor(locale, {
                  key: 'url',
                  url: route,
                  queryParams,
                })
              } else {
                map[locale] = urlFor(locale, {
                  key: SystemPageKey.SearchResults,
                  queryParams: {
                    ...queryParams,
                    machineType:
                      pageData.machineCategoryData?.machineType ?? undefined,
                    brand: pageData.machineCategoryData?.brand
                      ? [pageData.machineCategoryData.brand]
                      : undefined,
                    ...(pageData.machineCategoryData?.model
                      ? searchModelsToQueryParams(
                          pageData.machineCategoryData?.model
                        )
                      : {}),
                  },
                })
              }
            }
          } else {
            const routeMatchResult = matchSearchRoute(currentLocale, url)

            const pathMachineType = routeMatchResult?.machineTypeSlug
              ? getMachineTypeForMachineTypeSlug(
                  routeMatchResult.machineTypeSlug
                )
              : undefined

            for (const localeKey of Object.keys(Locale)) {
              const locale = localeKey as TLocale
              map[locale] = urlFor(locale, {
                key: SystemPageKey.SearchResults,
                brandSlug: routeMatchResult?.brandSlug ?? undefined,
                modelSlug: routeMatchResult?.modelSlug ?? undefined,
                machineTypeSlug:
                  routeMatchResult?.machineTypeSlug ===
                  hotDealsRoute[currentLocale]
                    ? hotDealsRoute[locale]
                    : undefined,
                queryParams: {
                  ...queryParams,
                  machineType:
                    pathMachineType ??
                    (queryParams.machineType as string | undefined) ??
                    undefined,
                },
              })
            }
          }
        }
        break
      }
    }

    return map
  }, [
    localizedUrlMap,
    pageData,
    asPath,
    currentLocale,
    getMachineTypeForMachineTypeSlug,
    query,
  ])

  return localizations
}
